import { useEffect, useState } from "react";
import { Bookmark, ChevronDown, LogOut, Menu, X } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

const Navbar = () => {
  const { user, role, logOut } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navLinks = [
    { path: "/explore", label: "Explore" },
    { path: "/internships", label: "Internships" },
    { path: "/hackathons", label: "Hackathons" },
    { path: "/placements", label: "Placements" },
  ];

  useEffect(() => setMobileMenuOpen(false), [location.pathname]);

  const isActive = (path) => location.pathname === path;
  const handleLogout = async () => { await logOut(); navigate("/"); };

  return (
    <header className="sticky top-0 z-50 border-b border-slate-200/80 bg-white/90 backdrop-blur-xl">
      <nav className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-5 sm:px-8 lg:px-10" aria-label="Main navigation">
        <Link to="/" className="group flex items-center gap-2.5" aria-label="CareerHorizon home">
          <span className="brand-mark transition-transform group-hover:-rotate-3"><span>CH</span></span>
          <span className="text-lg font-extrabold tracking-[-0.03em] text-slate-950">Career<span className="text-indigo-600">Horizon</span></span>
        </Link>

        <div className="hidden items-center gap-1 lg:flex">
          {navLinks.map((link) => (
            <Link key={link.path} to={link.path} className={`nav-link ${isActive(link.path) ? "nav-link-active" : ""}`}>
              {link.label}
            </Link>
          ))}
          {role === "admin" && <Link to="/manage-opportunities" className={`nav-link ${location.pathname.startsWith("/manage") ? "nav-link-active" : ""}`}>Admin</Link>}
        </div>

        <div className="hidden items-center gap-2 lg:flex">
          {user ? (
            <>
              <Link to="/saved-internships" className="button btn-ghost gap-2 px-3"><Bookmark size={17} /> Saved</Link>
              <div className="group relative">
                <button className="flex items-center gap-2 rounded-full border border-slate-200 bg-white py-1.5 pl-1.5 pr-3 text-sm font-semibold text-slate-700 hover:border-slate-300">
                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-indigo-100 font-bold text-indigo-700">{user.displayName?.charAt(0)?.toUpperCase() || "U"}</span>
                  <span className="max-w-24 truncate">{user.displayName?.split(" ")[0] || "Account"}</span>
                  <ChevronDown size={14} />
                </button>
                <div className="invisible absolute right-0 top-full pt-2 opacity-0 transition group-focus-within:visible group-focus-within:opacity-100 group-hover:visible group-hover:opacity-100">
                  <button onClick={handleLogout} className="flex w-36 items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-red-600 shadow-xl hover:bg-red-50"><LogOut size={16} /> Sign out</button>
                </div>
              </div>
            </>
          ) : (
            <>
              <Link to="/login" className="button btn-ghost px-4">Sign in</Link>
              <Link to="/login" className="button btn-primary px-5">Get started</Link>
            </>
          )}
        </div>

        <button className="flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 text-slate-700 lg:hidden" onClick={() => setMobileMenuOpen((open) => !open)} aria-expanded={mobileMenuOpen} aria-label="Toggle navigation">
          {mobileMenuOpen ? <X size={21} /> : <Menu size={21} />}
        </button>
      </nav>

      {mobileMenuOpen && (
        <div className="border-t border-slate-100 bg-white px-5 pb-5 pt-3 shadow-xl lg:hidden">
          <div className="mx-auto max-w-7xl space-y-1">
            {navLinks.map((link) => <Link key={link.path} to={link.path} className={`block rounded-xl px-4 py-3 text-sm font-semibold ${isActive(link.path) ? "bg-indigo-50 text-indigo-700" : "text-slate-600"}`}>{link.label}</Link>)}
            {role === "admin" && <Link to="/manage-opportunities" className="block rounded-xl px-4 py-3 text-sm font-semibold text-slate-600">Admin panel</Link>}
            <div className="mt-3 border-t border-slate-100 pt-4">
              {user ? (
                <div className="flex items-center justify-between px-2"><Link to="/saved-internships" className="button btn-secondary gap-2"><Bookmark size={16} /> Saved</Link><button onClick={handleLogout} className="button text-red-600"><LogOut size={16} className="mr-2" /> Sign out</button></div>
              ) : (
                <div className="grid grid-cols-2 gap-3"><Link to="/login" className="button btn-secondary">Sign in</Link><Link to="/login" className="button btn-primary">Get started</Link></div>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
